import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MemberRegistrationTypeClient, MemberRegistrationTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new MemberRegistrationTypeClient(API_BASE_URL, axiosInstance);

// Cached query for dropdowns
export const useMemberRegistrationTypes = () => {
    return useQuery({
        queryKey: ['memberRegistrationTypes'],
        queryFn: async () => {
            const response = await apiClient.searchMemberRegistrationTypes({
                page: 1,
                pageSize: MAX_DROPDOWN_PAGE_SIZE,
                sortBy: 'DisplayPosition',
            });
            return response?.data?.results || [];
        },
        staleTime: 4 * 60 * 60 * 1000, // 4 hours
        refetchOnReconnect: true,
    });
};

// Uncached search for admin tables
export const useMemberRegistrationTypeSearch = () => {
    const search = async (params: any) => {
        const response = await apiClient.searchMemberRegistrationTypes(params);
        return {
            results: response?.data?.results || [],
            totalCount: response?.data?.totalCount || 0,
        };
    };

    return { search };
};

// Query by ID
export const useMemberRegistrationTypeById = (id: string) => {
    return useQuery({
        queryKey: ['memberRegistrationType', id],
        queryFn: async () => {
            const response = await apiClient.getMemberRegistrationTypeById(id);
            return response?.data || null;
        },
        enabled: !!id,
    });
};

// Create mutation
export const useCreateMemberRegistrationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: MemberRegistrationTypeDTO) => {
            return await apiClient.createMemberRegistrationType(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationTypes'] });
        },
    });
};

// Update mutation
export const useUpdateMemberRegistrationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: MemberRegistrationTypeDTO }) => {
            return await apiClient.updateMemberRegistrationType(id, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationTypes'] });
        },
    });
};

// Delete mutation
export const useDeleteMemberRegistrationType = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            return await apiClient.deleteMemberRegistrationType(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['memberRegistrationTypes'] });
        },
    });
};